/* eslint-disable sonarjs/cognitive-complexity */
import React from "react";
import { Row, CardBody, Card, Container } from "reactstrap";
import { AvForm } from "availity-reactstrap-validation";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
    StyledButton,
    StyledLabel,
    StyledTextInput,
} from "@aureskonnect/react-ui";

import ShowPasswordSvgIcon from "@components/Common/SvgIcons/ShowPasswordSvgIcon";
import HidePasswordSvgIcon from "@components/Common/SvgIcons/HidePasswordSvgIcon";
import PortalLogoSvgIcon from "@components/Common/SvgIcons/PortalLogoSvgIcon";

import { setDominantColor } from "@store/features/colorSlice";
import { useDispatch } from "react-redux";
import PageTitle from "@components/Common/PageTitle";
import NProgressWrapper from "@components/Common/NProgressWrapper";
import { BloqArchiveAccountLogin } from "./BloqArchiveAccountLogin";

import ForgotPasswordModal from "./ForgotPasswordModal";

import { cryptWord, decryptWord } from "./Crypto";

import "./Login.css";

export default function Login() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [isPasswordDisplayed, setIsPasswordDisplayed] =
        React.useState<boolean>(false);

    const [messageError, setMessageError] = React.useState<string>(
        // eslint-disable-next-line sonarjs/no-duplicate-string
        t("This field is required")
    );
    const [messagePwdError, setMessagePwdError] = React.useState<string>(
        t("This field is required")
    );

    const [inputEmail, setInputEmail] = React.useState<string>("");
    const [inputPwd, setInputPwd] = React.useState<string>("");

    const [email, setEmail] = React.useState<string>("");
    const [passWord, setPassWord] = React.useState<string>("");

    const [isActiveUserModalOpned, setIsActiveUserModalOpned] =
        React.useState<boolean>(false);
    const [msgTitleModal, setMsgTitleModal] = React.useState<string>("");
    const [isForgotPasswordClick, setIsForgotPasswordClick] =
        React.useState<boolean>(false);

    const [, setIsFocusInInputs] = React.useState<boolean>(false);

    const [isInvalidPassword] = React.useState<boolean>(false);
    React.useEffect(() => {
        if (inputPwd.length === 0 && !isInvalidPassword) {
            setMessagePwdError(t("This field is required"));
        } else {
            setMessagePwdError(t("Incorrect password"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputPwd]);

    async function getAdyenEntityInfo(uuidEntity: string): Promise<any> {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_ADMIN_ACCESS}/getAdyenInfo?uidentitie=${uuidEntity}`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                }
            );
 
            const data = await response.json();
           
            if (!data.error && data.data !== null) {
           
                localStorage.setItem(
                    "holderAccountId",
                    data.data.accountHolderId
                );
            }
          
            return data;
        } catch (error) {
            return { error: true };
        }
    }

    async function handleValidSubmit() {
        setIsFocusInInputs(false);

        if (!isForgotPasswordClick) {
            const urlVerifyUserAccount = `${process.env.REACT_APP_ADMIN_ACCESS}/customerAccount`;

            try {
                const result = await fetch(urlVerifyUserAccount, {
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                    body: `email=${encodeURIComponent(
                        email
                    )}&password=${encodeURIComponent(passWord)}`,
                    method: "POST",
                });

                const response = await result.json();

                if (response.data?.email && !response.error) {
                    const adyenData = await getAdyenEntityInfo(
                        response.data.uidentitie
                    ); 
                   
                    if (response.data.etataccount === "Activer") {
                       
                        localStorage.setItem(
                            "Uid_Entitie_User",
                            response.data.uidentitie
                        );
                        localStorage.setItem(
                            "firstName",
                            response.data.firstname
                        );
                        localStorage.setItem("lastName", response.data.name);
                        localStorage.setItem(
                            "uuidprofile",
                            response.data.uuidprofile
                        );
                        localStorage.setItem("email", email);
                        localStorage.setItem("User_Email", cryptWord(email));
                        localStorage.setItem(
                            "secondaryColor",
                            response.data.secondarycolor
                        );
                        localStorage.setItem(
                            "mainColor",
                            response.data.maincolor
                        ); 
                        dispatch(setDominantColor(response.data.maincolor));
                        if (adyenData.error || adyenData.data === null || adyenData.data.blocked ) {
                            localStorage.setItem("authorized", adyenData.error || adyenData.data === null || adyenData.data.blocked);
                            navigate("/unauthorizedaccess");
                        } else{
                            navigate("/Reporting/transactions");
                        }
                      
                    } else {
                        setIsActiveUserModalOpned(true);
                        localStorage.setItem("email", email);
                        localStorage.setItem("User_Email", cryptWord(email));

                        const modalMessage =
                            response.data.accounttype === "ADMIN"
                                ? "Vous n'êtes plus autorisé à utiliser ce compte. Veuillez contacter notre service support."
                                : "Vous n'êtes plus autorisé à utiliser ce compte. Veuillez contacter votre administrateur.";
                        setMsgTitleModal(modalMessage);
                    }
                } else {
                    const decryptEmail = localStorage.getItem("email")
                        ? decryptWord(localStorage.getItem("email")!)
                        : "";
                    const decryptPwd = localStorage.getItem("email")
                        ? decryptWord(localStorage.getItem("email")!)
                        : "";

                    if (email === decryptEmail && passWord !== decryptPwd) {
                        setMessagePwdError("Incorrect password");
                        setInputPwd(" ");
                    } else if (
                        email !== decryptEmail &&
                        passWord === decryptPwd
                    ) {
                        setMessageError(
                            "Compte introuvable. Vérifiez votre adresse e-mail."
                        );
                        setInputEmail(" ");
                    }
                }
            } catch (error) {
                setLoading(true);
            } finally {
                setLoading(false);
            }
        }
    }

    function toggleOpeningForgotPasswordModal() {
        setIsModalOpened(!isModalOpened);
    }

    function handleCreateAccountButtonOnClickEvent() {
        navigate("registration");
    }

    React.useEffect(() => {
        const uidEntity = localStorage.getItem("Uid_Entitie_User");

        if (uidEntity !== null && window.location.pathname === "/") {
            navigate("/Reporting/transactions");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <React.Fragment>
            <PageTitle title={t("Login")} />
            <video
                autoPlay
                loop
                muted
                style={{
                    zIndex: 0,
                }}
                className="video position-fixed float-right"
            >
                <source src="background.mp4" type="video/mp4" />
            </video>
            <Container fluid className="p-0 m-0 overflow-hidden">
                <NProgressWrapper isLoading={loading} />
                <Row>
                    <div
                        className="white-section"
                        style={{
                            height: "100vh",
                            backgroundColor: "white",
                        }}
                    >
                        <div className="d-flex justify-content-center align-items-center">
                            <PortalLogoSvgIcon
                                fill="black"
                                className="etk-logo__clz"
                            />
                        </div>
                    </div>
                    <Card
                        className="overflow-hidden login-card w-sm-80 w-md-60 w-lg-50 w-xl-40 w-xxl-30"
                        style={{
                            // eslint-disable-next-line sonarjs/no-duplicate-string
                            boxShadow: "rgba(0, 0, 0, 0.42) -5px 3px 12px -2px",
                            WebkitBoxShadow:
                                "rgba(0, 0, 0, 0.42) -5px 3px 12px -2px",
                            MozBoxShadow:
                                "rgba(0, 0, 0, 0.42) -5px 3px 12px -2px",
                            borderRadius: "2%",
                            position: "absolute",
                            transform: "translate(-50%, -50%)",
                            padding: "10px",
                            margin: "0px",
                        }}
                    >
                        <p
                            className="mb-2 mt-4 ml-4"
                            style={{
                                fontSize: "33px",
                                fontWeight: 500,
                            }}
                        >
                            {t("Login")}
                        </p>
                        <CardBody className="ml-1 py-0">
                            {/* <div> */}
                            <AvForm
                                style={{ position: "relative" }}
                                className="form-horizontal"
                                onValidSubmit={(e: any, v: any) => {
                                    setEmail(v.email);
                                    setPassWord(v.password);

                                    if (v.password === "" || v.email === "") {
                                        if (v.password === "") {
                                            setMessagePwdError(
                                                t("This field is required")
                                            );
                                        }
                                        if (v.email === "") {
                                            setMessageError(
                                                t("This field is required")
                                            );
                                        }
                                    } else {
                                        handleValidSubmit();
                                    }
                                }}
                                onKeyDown={(e: any) => {
                                    if (e.key === "Enter") e.preventDefault();
                                }}
                            >
                                <div className="margin_b">
                                    <StyledLabel htmlFor="email">
                                        {t("Username")}
                                    </StyledLabel>
                                    <StyledTextInput
                                        id="email"
                                        name="email"
                                        type="email"
                                        className="form-control__clz"
                                        placeholder={t("Username")}
                                        onFocus={() => setIsFocusInInputs(true)}
                                        onChange={(e: any) => {
                                            setEmail(e.target.value);
                                            setInputEmail(e.target.value);
                                        }}
                                        value={inputEmail}
                                        validate={{
                                            email: {
                                                value: true,
                                                errorMessage: messageError,
                                            },
                                            required: {
                                                value: true,
                                                errorMessage: messageError,
                                            },
                                        }}
                                        onBlur={(inputText: any) => {
                                            const forbiddenCharactersRegex =
                                                // eslint-disable-next-line prefer-regex-literals
                                                new RegExp(
                                                    /^w+([.-]?w+)*@w+([.-]?w+)*(.w{2,3})+$/
                                                );

                                            if (
                                                !inputText.target.value.match(
                                                    forbiddenCharactersRegex
                                                )
                                            ) {
                                                setMessageError(
                                                    t(
                                                        "Please enter a valid username in the format name@example.com"
                                                    )
                                                );
                                            }

                                            if (
                                                inputText.target.value
                                                    .length === 0
                                            ) {
                                                setMessageError(
                                                    t("This field is required")
                                                );
                                            }
                                        }}
                                    />
                                </div>
                                <div className="password-toggle__clz">
                                    {isPasswordDisplayed ? (
                                        <HidePasswordSvgIcon
                                            onClick={() =>
                                                setIsPasswordDisplayed(
                                                    (prevState: boolean) =>
                                                        !prevState
                                                )
                                            }
                                        />
                                    ) : (
                                        <ShowPasswordSvgIcon
                                            onClick={() =>
                                                setIsPasswordDisplayed(
                                                    (prevState: boolean) =>
                                                        !prevState
                                                )
                                            }
                                        />
                                    )}
                                </div>

                                <div className="mb-4">
                                    <StyledLabel htmlFor="password">
                                        {t("Password")}
                                    </StyledLabel>
                                    <StyledTextInput
                                        id="password"
                                        name="password"
                                        type={
                                            isPasswordDisplayed
                                                ? "text"
                                                : "password"
                                        }
                                        onFocus={() => setIsFocusInInputs(true)}
                                        value={inputPwd}
                                        validate={{
                                            required: {
                                                value: true,
                                                errorMessage: messagePwdError,
                                            },
                                        }}
                                        onChange={(e: any) => {
                                            setPassWord(e.target.value);
                                            setInputPwd(e.target.value);
                                        }}
                                        onBlur={(inputText: any) => {
                                            if (
                                                inputText.target.value
                                                    .length === 0
                                            ) {
                                                setMessageError(
                                                    t("This field is required")
                                                );
                                            }
                                        }}
                                        placeholder={t("Password")}
                                        className="form-control__clz"
                                    />
                                </div>

                                <div className="d-flex justify-content-end  ">
                                    <StyledButton
                                        variant="link"
                                        className=""
                                        style={{
                                            textDecoration: "underline",
                                            color: "#495057",
                                            background: "transparent",
                                            position: "fixed !important",
                                        }}
                                        onClick={(e: any) => {
                                            e.stopPropagation();
                                            setIsForgotPasswordClick(true);
                                            toggleOpeningForgotPasswordModal();
                                        }}
                                    >
                                        {t("Forgot your password?")}
                                    </StyledButton>
                                </div>

                                <div className="d-flex justify-content-center  ">
                                    <StyledButton
                                        variant="primary"
                                        rounded
                                        outline={false}
                                        className="text-uppercase"
                                        type="submit"
                                    >
                                        {t("Login")}
                                    </StyledButton>
                                </div>

                                <div className="d-none">
                                    <StyledButton
                                        variant="link"
                                        style={{
                                            textDecoration: "underline",
                                            color: "#495057",
                                            background: "transparent",
                                        }}
                                        // eslint-disable-next-line react/jsx-no-bind
                                        onClick={
                                            handleCreateAccountButtonOnClickEvent
                                        }
                                    >
                                        {t("Create new account")}
                                    </StyledButton>
                                </div>
                            </AvForm>
                            {/* </div> */}
                        </CardBody>
                    </Card>
                </Row>
            </Container>
            <ForgotPasswordModal
                isModalOpened={isModalOpened}
                // eslint-disable-next-line react/jsx-no-bind
                toggleOpeningForgotPasswordModal={
                    toggleOpeningForgotPasswordModal
                }
                setIsForgotPasswordClick={setIsForgotPasswordClick}
                initEmail={email}
            />
            <BloqArchiveAccountLogin
                isModalOpened={isActiveUserModalOpned}
                setIsModalOpened={setIsActiveUserModalOpned}
                msgTitleModal={msgTitleModal}
            />
            ;
        </React.Fragment>
    );
}
